import { createSelector } from "@reduxjs/toolkit";
import { isEqual, pickBy } from "lodash-es";

import { EditUI, selectEditMode } from "@/features/app/appSlice";
import { selectConfigItem } from "@/features/config/configSlice";

import {
  selectCurrentBuilding,
  selectCurrentBuildingOverlayFeatures,
  selectCurrentBuildingPathDict,
  selectCurrentFloorID,
} from "@/features/cartography/cartographySlice.selectors";
import {
  deepMergeFeatureData,
  deepMergeKeepArrays,
} from "@/features/editor/components/overlay_layers/utils";
import { analyzePathProblems } from "@/features/editor/components/paths/actions";
import type {
  GenericOverlayFeature,
  OverlayFeatureTypes,
  SliceProps,
} from "@/features/editor/types";
import { filterOverlayFeatures } from "@/features/editor/utils/overlayFeaturesUtils";
import { DEFAULT_Z_POSITION } from "@/features/map/layers/PathLayer";
import { isEmptyDict } from "@/features/utils/utils";
import type {
  PathDictLinkItem,
  PathDictLinkItemForVisualization,
  PathDictNodeItem,
  PathsDataForVisualization,
} from "@/libs/models/path";
import type { RootState } from "../../app/store";

// Selectors
const sliceData = (state: RootState) => state.editor;

export const selectSelectedFeatureId = createSelector(
  sliceData,
  (data) => data.overlaysEditor.selectedFeatureId,
);

export const selectEditorFilterText = createSelector(
  sliceData,
  (data) => data.filterText,
);

export const selectPathsTemporaryNode = createSelector(
  sliceData,
  (data) => data.pathsEditor.temporaryNode,
);

export const selectProfilesList = createSelector(
  sliceData,
  (data) => data.profiles.list,
);

export const selectCurrentProfileNewName = createSelector(
  sliceData,
  (data) => data.profiles.newName,
);

export const selectFormError = (field: string) =>
  createSelector(
    sliceData,
    (data) => data.overlaysEditor.formErrors[field] ?? null,
  );

export const selectHasFormErrors = createSelector(
  sliceData,
  (data) => Object.keys(data.overlaysEditor.formErrors).length > 0,
);

// TODO undo-redo: migrate overlayLayers
export const selectEditorHasAnyChange = createSelector(
  sliceData,
  (data) =>
    Object.values(data.changes).some((x) => !isEmptyDict(x)) ||
    Object.values(data.pathsEditor.changesHistory.present).some(
      (x) => !isEmptyDict(x),
    ),
);

// OverlayLayers editor
export const selectLayersVisibility = createSelector(
  sliceData,
  (data) => data.overlaysEditor.visible,
);

export const selectChanges = createSelector(
  [sliceData, (_: RootState, key: EditUI) => key],
  (data, key) => {
    switch (key) {
      case EditUI.paths:
        return data.pathsEditor.changesHistory.present;
      case EditUI.overlayLayers:
        return data.changes.overlayFeatures;
      default:
        return {};
    }
  },
);

// TODO undo-redo: should return true if there are changes in the history???
export const selectHasChanges = createSelector(
  selectChanges,
  (changes) => Object.keys(changes).length > 0,
);

export const selectActiveOverlayFeatures = createSelector(
  [
    selectCurrentBuildingOverlayFeatures,
    selectEditMode,
    (state) => selectChanges(state, EditUI.overlayLayers),
  ],
  (buildingOverlayFeatures, editMode, overlayFeatureEdits) => {
    if (editMode == null || Object.keys(overlayFeatureEdits).length === 0) {
      return buildingOverlayFeatures;
    }

    const mergedFeatures = { ...buildingOverlayFeatures };
    Object.entries(overlayFeatureEdits).forEach(([id, edit]) => {
      if (edit.type === "new") {
        mergedFeatures[id] = edit.item!;
      } else if (edit.type === "updated" && mergedFeatures[id]) {
        mergedFeatures[id] = deepMergeFeatureData(
          mergedFeatures[id],
          edit.item,
        );
      } else if (edit.type === "deleted") {
        delete mergedFeatures[id];
      }
    });

    return mergedFeatures;
  },
);

export const selectFilteredOverlayFeatures = createSelector(
  [selectActiveOverlayFeatures, selectLayersVisibility, selectEditorFilterText],
  (overlayFeatures, layersVisibility, filterText) => {
    const visibleFeatureTypes = Object.entries(layersVisibility)
      .filter(([_, value]) => value)
      .map(([key]) => key as OverlayFeatureTypes);

    return filterOverlayFeatures(
      overlayFeatures,
      visibleFeatureTypes,
      filterText,
    );
  },
);

export const selectVisibleOverlayFeatures = createSelector(
  [selectFilteredOverlayFeatures, selectCurrentFloorID],
  (filteredFeatures, currentFloorID) => {
    if (!filteredFeatures) {
      return {};
    }

    return pickBy(filteredFeatures, (value: GenericOverlayFeature) => {
      if (!currentFloorID) return false;

      // If no floor id, always visible
      if (
        value.properties?.floorId == null &&
        value.properties?.floorLevel == null
      ) {
        return true;
      }

      // Return all features in the current floor
      return value.properties.floorId === currentFloorID;
    });
  },
);

export const selectSelectedFeature = createSelector(
  [selectActiveOverlayFeatures, selectSelectedFeatureId],
  (overlayFeatures, selectedFeatureId) => {
    if (!overlayFeatures || !selectedFeatureId) return undefined;
    return overlayFeatures[selectedFeatureId];
  },
);

export const selectActivePathsDict = createSelector(
  [
    selectCurrentBuildingPathDict,
    selectEditMode,
    (state) => selectChanges(state, EditUI.paths),
  ],
  (buildingPaths, editMode, changes) => {
    const mergedPaths = { ...buildingPaths };

    // Apply changes if any, and if in edit mode
    if (editMode === EditUI.paths && Object.keys(changes).length !== 0) {
      Object.entries(changes).forEach(([id, edit]) => {
        if (edit.type === "new") {
          mergedPaths[id] = edit.item;
        } else if (edit.type === "updated" && mergedPaths[id]) {
          mergedPaths[id] = deepMergeKeepArrays(mergedPaths[id], edit.item);
        } else if (edit.type === "deleted") {
          delete mergedPaths[id];
        }
      });
    }

    // Add metadata to each node
    for (const key in mergedPaths) {
      // Only process links.
      if (mergedPaths[key].type !== "link") continue;
      const item = mergedPaths[key] as PathDictLinkItem;

      // Create copies of both the source and target node
      const source = Object.assign(
        {},
        mergedPaths[item.source],
      ) as PathDictNodeItem;
      const target = Object.assign(
        {},
        mergedPaths[item.target],
      ) as PathDictNodeItem;

      // Update metadata for source node
      if (!source.metadata) {
        source.metadata = {
          attachedLinks: {},
        };
      }
      source.metadata.attachedLinks[item.id] = {
        targetFloor: target.floorId == source.floorId ? null : target.floorId,
      };

      // Update metadata for target node
      if (!target.metadata) {
        target.metadata = {
          attachedLinks: {},
        };
      }
      target.metadata.attachedLinks[item.id] = {
        targetFloor: target.floorId == source.floorId ? null : source.floorId,
      };

      // Store in mergedPaths
      mergedPaths[item.source] = source;
      mergedPaths[item.target] = target;
    }

    return mergedPaths;
  },
);

export const selectPathsDictStats = createSelector(
  [selectActivePathsDict],
  (pathsDict) => {
    return Object.values(pathsDict ?? {}).reduce(
      (acc, item) => {
        if (item.type === "node") {
          acc.nodes += 1;
        } else if (item.type === "link") {
          acc.links += 1;
        }

        return acc;
      },
      { links: 0, nodes: 0 },
    );
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectActivePathsDictIsEmpty = createSelector(
  [selectActivePathsDict],
  (pathsDict) => {
    if (!pathsDict) return true;
    return Object.values(pathsDict).length === 0;
  },
);

export const selectVisiblePathsDict = createSelector(
  [selectActivePathsDict, selectCurrentFloorID],
  (paths, currentFloorID) => {
    if (!paths) {
      return {};
    }

    return pickBy(paths, (value) => {
      if (!currentFloorID) return false;
      return value.floorId === currentFloorID;
    });
  },
);

export const selectPathsEditorSelectedFeatureId = createSelector(
  sliceData,
  (data) => data.pathsEditor.selectedFeatureId,
);

export const selectPathsEditorSelectedFeature = createSelector(
  [selectActivePathsDict, selectPathsEditorSelectedFeatureId],
  (pathsDict, featureId) => pathsDict?.[featureId],
);

export const selectPathsEditorSelectedFeatureProperties = createSelector(
  [selectActivePathsDict, selectPathsEditorSelectedFeatureId],
  (pathsDict, featureId) => {
    const feature = pathsDict?.[featureId];
    if (!feature) return null;
    // Return links unchanged
    if (feature.type === "link") return feature;

    // Return nodes without position information
    const { lat: _lat, lng: _lng, x: _x, y: _y, ...rest } = feature;
    return rest;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectPathsAnalyzeProblematicNodes = createSelector(
  sliceData,
  (state) => state.pathsEditor.settings.analyzeProblematicNodes,
);

export const selectPathsDisplayNodeIds = createSelector(
  sliceData,
  (state) => state.pathsEditor.settings.displayNodeIds,
);

export const selectPathsDisplayFloorChanges = createSelector(
  sliceData,
  (state) => state.pathsEditor.settings.displayFloorChanges,
);

export const selectPathsDisplayOverGeojson = createSelector(
  sliceData,
  (state) => state.pathsEditor.settings.displayOverGeojson,
);

export const selectPathsEditorTool = createSelector(
  sliceData,
  (state) => state.pathsEditor.tool,
);

/**
 * Get a list of all used tags in the current version of the paths
 * dictionary. The tags are sorted alphabetically before being returned.
 */
export const selectPathLinkUsedTags = createSelector(
  selectActivePathsDict,
  (activePathsDict) => {
    const uniqueTags = Object.values(activePathsDict).reduce(
      (acc: Set<string>, item) => {
        if (item.type === "link") {
          item.tags.forEach((tag) => acc.add(tag));
        }
        return acc;
      },
      new Set<string>(),
    );

    return [...uniqueTags].sort();
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

/**
 * Selector to return the largest node ID available to use.
 *
 * This is needed because, when creating new nodes, we need to
 * associate a new ID for said node, and.... the responsability of
 * adding said unique ID is... ours?? Backend requires that each
 * node has a unique ID before uploading, so it forces us to create
 * said ID manually in frontend :(
 */
export const selectPathsHighestNodeId = createSelector(
  [selectActivePathsDict],
  (pathsDict) => {
    const currentHighest = Object.values(pathsDict ?? {})
      .filter((x) => x.type == "node")
      .sort((a, b) => parseInt(b.id) - parseInt(a.id))
      .at(0)?.id;

    return (parseInt(currentHighest ?? "0") + 1).toString();
  },
);

export const selectPathProblems = createSelector(
  [
    selectActivePathsDict,
    selectCurrentBuilding,
    selectPathsAnalyzeProblematicNodes,
  ],
  (pathsDict, currentBuilding, analyzeProblems) => {
    return analyzePathProblems(pathsDict, currentBuilding, 2, !analyzeProblems);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectCurrentFloorPathsProblems = createSelector(
  [selectPathProblems, selectCurrentFloorID],
  (pathsAnalysis, floorId) => {
    return pathsAnalysis.filter((x) => x.floorId == floorId);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

export const selectHasNodesOutsideBuilding = createSelector(
  [selectPathProblems],
  (pathsAnalysis) => {
    return pathsAnalysis.some((x) => x.problemType == "outside");
  },
);

export const selectPathsEditorSelectedFeatureProblem = createSelector(
  [selectPathProblems, selectPathsEditorSelectedFeatureId],
  (pathsDict, featureId) => {
    return pathsDict.find((x) => x.id == featureId);
  },
);

/**
 * Determine the tag type for a link based on the provided and excluded tags.
 */
const determineTagTypeForLink = (
  link: PathDictLinkItem,
  includedTags: string[],
  excludedTags: string[],
) => {
  const filteredTags = link.tags?.filter((tag) => !tag.startsWith("#")) ?? [];

  const hasTags = filteredTags.length > 0;
  const matchesIncluded = includedTags?.length
    ? filteredTags.some((t) => includedTags?.includes(t))
    : false;
  const matchesExcluded = excludedTags?.length
    ? filteredTags.some((t) => excludedTags?.includes(t))
    : false;

  if (includedTags && includedTags.length) {
    // When include tags are specified, the link is "included" if it matches.
    if (matchesIncluded) return "included";
    // Otherwise, if the link is untagged but we want to mark untagged as excluded...
    if (!hasTags && excludedTags?.includes("notag")) return "excluded";
    // If the link has tags and they match excluded criteria, mark as excluded.
    // if (hasTags && matchesExcluded) return "excluded";
    if (hasTags) return "excluded";
  } else {
    // When no include tags are specified:
    // If the link has tags and isn’t excluded, treat it as "included".
    if (hasTags && !matchesExcluded) return "included";
    // If it’s untagged and "notag" is in the excluded list, mark as "excluded".
    if (!hasTags && excludedTags?.includes("notag")) return "excluded";
    // If it has tags and those match excluded, mark as "excluded".
    if (hasTags && matchesExcluded) return "excluded";
  }
  return undefined;
};

export const selectPathsDataForVisualization = createSelector(
  [
    selectActivePathsDict,
    selectVisiblePathsDict,
    (state) => selectConfigItem("directions.includedTags")(state),
    (state) => selectConfigItem("directions.excludedTags")(state),
    selectCurrentBuilding,
    selectCurrentFloorID,
    selectPathsDisplayFloorChanges,
  ],
  (
    wholePathsDict,
    visiblePathsDict,
    includedTags,
    excludedTags,
    currentBuilding,
    currentFloorID,
    displayFloorChanges,
  ) => {
    // Initialize empty data object
    const data: PathsDataForVisualization = {
      floorChangeFromNodes: [],
      floorChangeLinks: [],
      floorChangeToNodes: [],
      normalLinks: [],
      normalNodes: [],
      notAccessibleLinks: [],
    };

    // If not input data, return
    if (!wholePathsDict || !visiblePathsDict) {
      return data;
    }

    const linkIdsWithFloorChanges = new Set<string>();

    // Add nodes and links to the data object
    for (const item of Object.values(visiblePathsDict)) {
      if (item.type === "node") {
        const floorChangeLinks = Object.entries(
          item.metadata?.attachedLinks ?? {},
        ).filter(([_, value]) => value.targetFloor != null);

        if (floorChangeLinks.length === 0) {
          // Put normal nodes to its array
          data.normalNodes.push(item);
        } else {
          data.floorChangeFromNodes.push(item);

          // Create a set with all links that have a floor change linked to this floor
          floorChangeLinks.forEach(([key, _]) => {
            linkIdsWithFloorChanges.add(key);
          });
        }
      } else if (item.type === "link") {
        // Get source and target coordinates for each link
        const sourceCoordinates = {
          lat: (wholePathsDict[item.source] as PathDictNodeItem).lat,
          lng: (wholePathsDict[item.source] as PathDictNodeItem).lng,
        };
        const targetCoordinates = {
          lat: (wholePathsDict[item.target] as PathDictNodeItem).lat,
          lng: (wholePathsDict[item.target] as PathDictNodeItem).lng,
        };

        const computedLink: PathDictLinkItemForVisualization = {
          ...item,
          from: sourceCoordinates,
          tagType: determineTagTypeForLink(item, includedTags, excludedTags),
          to: targetCoordinates,
        };

        if (computedLink.accessible) {
          data.normalLinks.push(computedLink);
        } else {
          data.notAccessibleLinks.push(computedLink);
        }
      }
    }

    if (!displayFloorChanges) return data;

    // Properly map links with floor changes data, as well as the node on the
    // destination floor
    linkIdsWithFloorChanges.forEach((linkId) => {
      const link = wholePathsDict[linkId] as PathDictLinkItem;
      const sourceNode = wholePathsDict[link.source] as PathDictNodeItem;
      const targetNode = wholePathsDict[link.target] as PathDictNodeItem;

      // Get level from the two floors affected by this link
      const sourceLevel = currentBuilding.getFloorById(
        sourceNode.floorId,
      ).level;
      const targetLevel = currentBuilding.getFloorById(
        targetNode.floorId,
      ).level;

      let linkHeight;
      if (sourceNode.floorId == currentFloorID) {
        linkHeight = (targetLevel - sourceLevel) * 6 || DEFAULT_Z_POSITION;
        data.floorChangeToNodes.push({ ...targetNode, z: linkHeight });
      } else {
        linkHeight = (sourceLevel - targetLevel) * 6 || DEFAULT_Z_POSITION;
        data.floorChangeToNodes.push({ ...sourceNode, z: linkHeight });
      }

      // Get source and target coordinates for each link
      const sourceCoordinates = {
        lat: sourceNode.lat,
        lng: sourceNode.lng,
        z:
          sourceNode.floorId == currentFloorID
            ? DEFAULT_Z_POSITION
            : linkHeight,
      };
      const targetCoordinates = {
        lat: targetNode.lat,
        lng: targetNode.lng,
        z:
          targetNode.floorId == currentFloorID
            ? DEFAULT_Z_POSITION
            : linkHeight,
      };
      data.floorChangeLinks.push({
        ...link,
        from: sourceCoordinates,
        fromFloorId: sourceNode.floorId,
        to: targetCoordinates,
        toFloorId: targetNode.floorId,
      });
    });

    return data;
  },
);

export const canUndo = createSelector(
  sliceData,
  (_, key: EditUI) => key,
  (data: SliceProps, key: EditUI) => {
    switch (key) {
      case EditUI.paths:
        return data.pathsEditor.changesHistory.past.length > 0;
      case EditUI.overlayLayers:
      // TODO undo-redo: implement overlayLayers
      default:
        return false;
    }
  },
);

export const canRedo = createSelector(
  sliceData,
  (_, key: EditUI) => key,
  (data: SliceProps, key: EditUI) => {
    switch (key) {
      case EditUI.paths:
        return data.pathsEditor.changesHistory.future.length > 0;
      case EditUI.overlayLayers:
      // TODO undo-redo: implement overlayLayers
      default:
        return false;
    }
  },
);
